import * as React from "react";
import { connect } from "react-redux";

import ActivityFollowUp from './activityFollowUp';
import ReadPassageContainer from './readPassageContainer';
import useEvidenceClipPrefetch from './useEvidenceClipPrefetch';
import RightPanel from './rightPanel';

import { BECAUSE, BUT, CHECKLIST, CLICK, INTRODUCTION, KEYDOWN, KEYPRESS, MOUSEDOWN, MOUSEMOVE, READ_AND_HIGHLIGHT, READ_PASSAGE_STEP_NUMBER, SO, VISIBILITYCHANGE, getParameterByName, roundMillisecondsToSeconds, safeStripHtml } from '../../../Shared/index';
import { getActivity } from "../../actions/activities";
import { TrackAnalyticsEvent } from "../../actions/analytics";
import { completeActivitySession, fetchActiveActivitySession, getFeedback, processUnfetchableSession, reportAProblem, saveActiveActivitySession, saveActivitySurveyResponse, setActiveStepForSession, setActivityIsCompleteForSession, setPreviewSessionStep } from '../../actions/session';
import { everyOtherStepCompleted, getActivitySteps, getCurrentStepDataForEventTracking, getLastSubmittedResponse, getStrippedPassageHighlights, getUrlParam, MINIMUM_STUDENT_HIGHLIGHT_COUNT, onMobile, orderedSteps, outOfAttemptsForActivePrompt, STUDENT_HIGHLIGHT_STARTS_TEXT, STUDENT_HIGHLIGHT_ENDS_TEXT } from '../../helpers/containerActionHelpers';
import { renderDirections } from '../../helpers/containerRenderHelpers';
import { generateConceptResults, generatePercentageScore, } from '../../libs/conceptResults';
import { Events } from '../../modules/analytics';
import { ActivitiesReducerState } from '../../reducers/activitiesReducer';
import { SessionReducerState } from '../../reducers/sessionReducer';
import { postTurkSession } from '../../utils/turkAPI';
import { sumTimeTracking } from '../../../Shared/utils/liveViewAPIs';
import { LiveViewProgress, createLiveViewProgress, evidenceAnsweredPrompts } from '../../../Shared/utils/liveViewProgress';
import { Spinner } from '../../../Shared/index';

interface StudentViewContainerProps {
  dispatch: Function;
  activities: ActivitiesReducerState;
  session: SessionReducerState;
  location?: any;
  handleFinishActivity?: () => void;
  isTurk?: boolean;
  user: string;
  userRole: string;
  previewMode: boolean;
}

interface StudentViewContainerState {
  completedSteps: Array<number>;
  isIdle: boolean;
  showFocusState: boolean;
  startTime: number;
  timeTracking: { [key:string]: number };
  studentHighlights: string[];
  doneHighlighting: boolean;
  showReadTheDirectionsButton: boolean;
  scrolledToEndOfPassage: boolean;
  hasStartedReadPassageStep: boolean;
  hasStartedPromptSteps: boolean;
}

const ONBOARDING = 'onboarding'
const MAX_PROMPT_ATTEMPTS = 5

// Mirrors the step key used for time tracking
const liveViewStage = (session, hasStartedReadPassageStep) => {
  if (!hasStartedReadPassageStep) { return 'onboarding' }
  if (session.activeStep === READ_PASSAGE_STEP_NUMBER) { return 'reading' }
  return 'prompts'
}

const getStepForConjunction = (activities, conjunction) => {
  const ordered = orderedSteps(activities)
  const index = ordered.findIndex(p => p.conjunction === conjunction)
  return index === -1 ? null : index + 2
}

export const StudentViewContainer = ({ dispatch, session, isTurk, location, activities, handleFinishActivity, user, previewMode, userRole, }: StudentViewContainerProps) => {
  const skipToSpecificStep = window.location.href.includes('skipToStep')
  const shouldSkipToPrompts = window.location.href.includes('turk') || window.location.href.includes('skipToPrompts') || skipToSpecificStep
  const defaultCompletedSteps = shouldSkipToPrompts ? [READ_PASSAGE_STEP_NUMBER] : []
  const sessionFromUrl = getUrlParam('session', location, isTurk)
  const activityUID = getUrlParam('uid', location, isTurk)

  const refs = {
    step1: React.useRef(),
    step2: React.useRef(),
    step3: React.useRef(),
    step4: React.useRef()
  }

  const inactivityTimer = React.useRef(null)

  const [showGoodToKnow, setShowGoodToKnow] = React.useState(false)
  const [completeButtonClicked, setCompleteButtonClicked] = React.useState(false)
  const [completedSteps, setCompletedSteps] = React.useState(defaultCompletedSteps)
  const [showFocusState, setShowFocusState] = React.useState(false)
  const [showStepsSummary, setShowStepsSummary] = React.useState(false)
  const [startTime, setStartTime] = React.useState(Date.now())
  const [isIdle, setIsIdle] = React.useState(false)
  const [studentHighlights, setStudentHighlights] = React.useState([])
  const [scrolledToEndOfPassage, setScrolledToEndOfPassage] = React.useState(shouldSkipToPrompts)
  const [hasStartedReadPassageStep, setHasStartedReadPassageStep] = React.useState(shouldSkipToPrompts)
  const [hasStartedPromptSteps, setHasStartedPromptsSteps] = React.useState(skipToSpecificStep)
  const [doneHighlighting, setDoneHighlighting] = React.useState(shouldSkipToPrompts)
  const [showReadTheDirectionsButton, setShowReadTheDirectionsButton] = React.useState(false)
  // onMobile()-dependent markup (e.g. the passage pane) needs a re-render when the viewport crosses
  // the breakpoint -- CSS media queries update on resize, React doesn't. Tracking the boundary does that.
  const [, setViewportIsMobile] = React.useState(onMobile())
  useEvidenceClipPrefetch(activities.currentActivity)
  const [timeTracking, setTimeTracking] = React.useState({
    [ONBOARDING]: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0
  })

  const liveViewProgressRef = React.useRef<LiveViewProgress|null>(null)
  const timeTrackingRef = React.useRef(timeTracking)
  timeTrackingRef.current = timeTracking
  const sessionRef = React.useRef(session)
  sessionRef.current = session
  const isIdleRef = React.useRef(isIdle)
  isIdleRef.current = isIdle
  const completedStepsRef = React.useRef(completedSteps)
  completedStepsRef.current = completedSteps
  const hasStartedReadPassageStepRef = React.useRef(hasStartedReadPassageStep)
  hasStartedReadPassageStepRef.current = hasStartedReadPassageStep
  const activitiesRef = React.useRef(activities)
  activitiesRef.current = activities

  React.useEffect(() => {
    const { activeStep } = session;
    if (Object.keys(session.submittedResponses).length && getStrippedPassageHighlights({ activities, session, activeStep })) {
      scrollToHighlight()
    }
  }, [session.submittedResponses])

  React.useEffect(() => {
    if (previewMode || !sessionFromUrl || liveViewProgressRef.current) { return }
    if (!activities.currentActivity) { return }

    liveViewProgressRef.current = createLiveViewProgress(sessionFromUrl, {
      totalPrompts: (activities.currentActivity.prompts || []).length || null,
      getTimeSpentMs: () => sumTimeTracking(timeTrackingRef.current),
      getStage: () => liveViewStage(sessionRef.current, hasStartedReadPassageStepRef.current),
      getIdle: () => isIdleRef.current,
      // Steps after the passage are the prompts, each timed under its step number;
      // a finished prompt is no longer being worked on, as in resetTimers
      getQuestionTimeSpentMs: () => {
        const { activeStep, } = sessionRef.current
        if (!hasStartedReadPassageStepRef.current || activeStep <= READ_PASSAGE_STEP_NUMBER) { return null }
        if (completedStepsRef.current.includes(activeStep)) { return null }
        if (outOfAttemptsForActivePrompt(activeStep, sessionRef.current, activitiesRef.current)) { return null }
        return timeTrackingRef.current[activeStep] || 0
      },
    })
  }, [activities.currentActivity])

  React.useEffect(() => {
    liveViewProgressRef.current && liveViewProgressRef.current.reportState()
  }, [hasStartedReadPassageStep, session.activeStep, isIdle])

  React.useEffect(() => {
    return () => { liveViewProgressRef.current && liveViewProgressRef.current.stop() }
  }, [])

  React.useEffect(() => {
    const liveViewProgress = liveViewProgressRef.current
    if (previewMode || !sessionFromUrl || !activities.currentActivity || !liveViewProgress) { return }
    // Wait for the saved session, so restored responses are reported in one go
    if (!session.hasReceivedData) { return }

    liveViewProgress.sync(evidenceAnsweredPrompts(session.submittedResponses, MAX_PROMPT_ATTEMPTS))
  }, [session.submittedResponses, session.hasReceivedData, activities.currentActivity])

  React.useEffect(() => {
    // Unchanged boolean is a no-op, so this only re-renders on an actual crossing.
    const handleResize = () => setViewportIsMobile(prevMobile => {
      const nowMobile = onMobile()
      // On a crossing, reset scroll: mobile scrolls the document, desktop scrolls an inner pane
      // (html/body overflow:hidden), so a stale document offset would leave the passage out of view.
      if (prevMobile !== nowMobile) { window.scrollTo(0, 0) }
      return nowMobile
    })
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  React.useEffect(() => {
    if (scrolledToEndOfPassage) { return }
    if (!hasStartedReadPassageStep && session.previewSessionStep !== READ_AND_HIGHLIGHT) { return }

    // The #end-of-passage sentinel is only rendered while !scrolledToEndOfPassage, so
    // this effect must also re-run when scrolledToEndOfPassage flips back to false (e.g.
    // when the teacher preview menu jumps into the read/highlight step) to reattach the observer.
    const el = document.getElementById('end-of-passage')
    if (!el) { return }

    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setScrolledToEndOfPassage(true) } });
    observer.observe(el);

    return () => observer.disconnect();
  }, [hasStartedReadPassageStep, session.previewSessionStep, scrolledToEndOfPassage]);

  React.useEffect(() => {
    if (sessionFromUrl) {
      const fetchActiveActivitySessionArgs = {
        sessionID: sessionFromUrl,
        activityUID: activityUID,
        callback: loadPreviousSession
      }
      dispatch(getActivity(sessionFromUrl, activityUID))
        .then(() => {
          dispatch(fetchActiveActivitySession(fetchActiveActivitySessionArgs))
        })
    } else {
      if (activityUID) {
        const { sessionID, } = session
        dispatch(getActivity(sessionID, activityUID))
        dispatch(processUnfetchableSession(sessionID));
        if (isTurk) {
          isTurk && handlePostTurkSession(sessionID);
        } else if (!skipToSpecificStep) {
          window.location.href = `${window.location.href}&session=${sessionID}`
        }
      }
    }
  }, [])

  // resetTimers picks its time bucket from hasStartedReadPassageStep, so the listeners re-bind when it flips.
  React.useEffect(() => {
    window.addEventListener(KEYDOWN, handleKeyDown)
    window.addEventListener(MOUSEMOVE, resetTimers)
    window.addEventListener(MOUSEDOWN, resetTimers)
    window.addEventListener(CLICK, handleClick)
    window.addEventListener(KEYPRESS, resetTimers)
    window.addEventListener(VISIBILITYCHANGE, setIdle)

    return function cleanup() {
      window.removeEventListener(KEYDOWN, handleKeyDown)
      window.removeEventListener(MOUSEMOVE, resetTimers)
      window.removeEventListener(MOUSEDOWN, resetTimers)
      window.removeEventListener(CLICK, handleClick)
      window.removeEventListener(KEYPRESS, resetTimers)
      window.removeEventListener(VISIBILITYCHANGE, setIdle)
    }
  }, [session, activities, hasStartedPromptSteps, hasStartedReadPassageStep, inactivityTimer, isIdle])

  React.useEffect(() => { activities.currentActivity ? document.title = `Quill.org | ${safeStripHtml(activities.currentActivity.title)}` : null }, [activities.currentActivity])

  React.useEffect(() => {
    if (!activities.currentActivity) { return }
    const allSteps = getActivitySteps(activities)
    const lastStep = allSteps[allSteps.length - 1]
    if (session.activeStep && session.activeStep > lastStep) {
      dispatch(setActiveStepForSession(lastStep))
    }
  }, [activities.currentActivity, session.activeStep])

  React.useEffect(() => {
    const { activeStep } = session;
    if (!outOfAttemptsForActivePrompt(activeStep, session, activities)) { return }

    if (!everyOtherStepCompleted(activeStep, completedSteps, activities)) { return }

    completeStep(activeStep)
  }, [session.submittedResponses])

  React.useEffect(() => {
    if (onMobile()) { window.scrollTo(0, 0) }
  }, [doneHighlighting])

  React.useEffect(() => {
    // Bail only when nothing has been completed yet. We intentionally don't compare against
    // defaultCompletedSteps.length: in Play Test Activity (skipToPrompts) the read step is
    // pre-completed, so that comparison wrongly blocked advancement when the teacher used the
    // preview menu to revisit and re-complete the read/highlight step with the menu hidden.
    if (completedSteps.length === 0 || !activities.currentActivity ) { return }

    const uniqueCompletedSteps = Array.from(new Set(completedSteps))
    const { activeStep } = session;

    // Don't go to the next step if we haven't flagged the current step as completed
    // This particular condition can occur when loading data from a partially-completed session which triggers this effect
    if (!uniqueCompletedSteps.includes(activeStep)) return

    const allSteps = getActivitySteps(activities)
    let nextStep: number|undefined = activeStep + 1
    if (nextStep > allSteps.length || uniqueCompletedSteps.includes(nextStep)) {
      nextStep = allSteps.find(s => !uniqueCompletedSteps.includes(s))
    }

    if (nextStep) {
      // don't activate a step if it's already active
      if (activeStep === nextStep) return
      // // don't activate nextSteps before Done reading button has been clicked
      if (nextStep && nextStep > 1 && !completedSteps.includes(READ_PASSAGE_STEP_NUMBER)) return

      dispatch(setActiveStepForSession(nextStep))
      setStartTime(Date.now())
      trackCurrentPromptStartedEvent()
      callSaveActiveActivitySession()
    } else {
      trackActivityCompletedEvent(); // If there is no next step, the activity is done
    }
  }, [completedSteps, activities.currentActivity])

  React.useEffect(() => {
    const { activeStep } = session;
    activeStep !== READ_PASSAGE_STEP_NUMBER ? scrollToStep(`step${activeStep}`) : null
  }, [session.activeStep])

  function handleReadPassageContainerScroll(e=null) {
    if (e) { resetTimers(e) }
  }

  function preparePreviewIntroductionStep() {
    dispatch(setPreviewSessionStep(INTRODUCTION))
    dispatch(setActiveStepForSession(1))
    dispatch(setActivityIsCompleteForSession(false))
    setShowGoodToKnow(true)
    setHasStartedReadPassageStep(false)
    setShowReadTheDirectionsButton(false)
    setScrolledToEndOfPassage(false)
    setDoneHighlighting(false)
    setHasStartedPromptsSteps(false)
    setCompleteButtonClicked(false)
    setCompletedSteps([])
  }

  function preparePreviewChecklistStep() {
    dispatch(setPreviewSessionStep(CHECKLIST))
    dispatch(setActiveStepForSession(1))
    dispatch(setActivityIsCompleteForSession(false))
    setShowGoodToKnow(false)
    setHasStartedReadPassageStep(false)
    setShowReadTheDirectionsButton(false)
    setScrolledToEndOfPassage(false)
    setDoneHighlighting(false)
    setHasStartedPromptsSteps(false)
    setCompleteButtonClicked(false)
    setCompletedSteps([])
  }

  function preparePreviewReadAndHighlightStep() {
    dispatch(setPreviewSessionStep(READ_AND_HIGHLIGHT))
    dispatch(setActiveStepForSession(1))
    dispatch(setActivityIsCompleteForSession(false))
    setHasStartedReadPassageStep(true)
    setShowReadTheDirectionsButton(true)
    setScrolledToEndOfPassage(false)
    setStudentHighlights([])
    setDoneHighlighting(false)
    setHasStartedPromptsSteps(false)
    setCompleteButtonClicked(false)
  }

  function preparePreviewConjunctionStep(conjunction: string) {
    const stepNumber = getStepForConjunction(activities, conjunction)
    if (!stepNumber) { return }
    dispatch(setPreviewSessionStep(conjunction))
    dispatch(setActiveStepForSession(stepNumber))
    dispatch(setActivityIsCompleteForSession(false))
    setHasStartedReadPassageStep(true)
    setShowReadTheDirectionsButton(false)
    setScrolledToEndOfPassage(true)
    setDoneHighlighting(true)
    setHasStartedPromptsSteps(true)
    setCompleteButtonClicked(false)
    setCompletedSteps(Array.from({ length: stepNumber - 1 }, (_, i) => i + 1))
  }

  function preparePreviewBecauseStep() { preparePreviewConjunctionStep(BECAUSE) }
  function preparePreviewButStep() { preparePreviewConjunctionStep(BUT) }
  function preparePreviewSoStep() { preparePreviewConjunctionStep(SO) }

  React.useEffect(() => {
    const { previewSessionStep } = session;

    if(previewSessionStep === INTRODUCTION) {
      preparePreviewIntroductionStep()
    } else if(previewSessionStep === CHECKLIST) {
      preparePreviewChecklistStep()
    } else if(previewSessionStep === READ_AND_HIGHLIGHT) {
      preparePreviewReadAndHighlightStep()
    } else if(previewSessionStep === BECAUSE) {
      preparePreviewBecauseStep()
    } else if (previewSessionStep === BUT) {
      preparePreviewButStep()
    } else if (previewSessionStep === SO) {
      preparePreviewSoStep()
    }

  }, [session.previewSessionStep])

  function handleReadTheDirectionsButtonClick(e) {
    // prevents firing when clicking back to read and highlight step again during preview mode
    if(!e && previewMode) { return }
    setShowReadTheDirectionsButton(false)
  }

  function resetTimers(e=null) {
    const now = Date.now()
    const { activeStep } = session;
    if (completedSteps.includes(activeStep)) { return } // don't want to add time if a user is revisiting a previously completed step
    if (outOfAttemptsForActivePrompt(activeStep, session, activities)) { return } // or if they are finished submitting responses for the current active step
    if (activeStep > READ_PASSAGE_STEP_NUMBER && !hasStartedPromptSteps) { return } // or if they are between the read passage step and starting the prompts

    let elapsedTime = now - startTime
    if (isIdle) {
      elapsedTime = 0
    }
    // Onboarding runs from opening the activity until Start on the Good to know page, which is
    // the moment the read passage step begins.
    const activeStepKey = hasStartedReadPassageStep ? activeStep : ONBOARDING
    const newTimeTracking = {...timeTracking, [activeStepKey]: timeTracking[activeStepKey] + elapsedTime}

    setTimeTracking(newTimeTracking)
    setIsIdle(false)
    setStartTime(now)
    inactivityTimerReset()

    return Promise.resolve(true);
  }

  function inactivityTimerReset() {
    if (inactivityTimer.current) { clearTimeout(inactivityTimer.current) }
    inactivityTimer.current = setTimeout(setIdle, 30000) // time is in milliseconds (1000 is 1 second)
  }

  function setIdle() {
    setIsIdle(true)
    setStartTime(Date.now())
    inactivityTimerReset()
  }

  function handlePostTurkSession(activitySessionId: string) {
    const turkingRoundID = getParameterByName('id', window.location.href);
    postTurkSession(turkingRoundID, activitySessionId).then(response => {
      const { error } = response;
      if(error) {
        alert(`${error}`);
      }
    });
  }

  function defaultHandleFinishActivity() {
    const { sessionID, submittedResponses, } = session
    const { currentActivity } = activities
    const conceptResults = generateConceptResults(currentActivity, submittedResponses)
    const percentage = generatePercentageScore(conceptResults)
    const orderedPrompts = orderedSteps(activities)
    const conjunctionTimes: { [key: string]: number } = {}
    orderedPrompts.forEach((prompt, index) => {
      conjunctionTimes[prompt.conjunction] = roundMillisecondsToSeconds(timeTracking[index + 2])
    })
    const data = {
      time_tracking: {
        onboarding: roundMillisecondsToSeconds(timeTracking[ONBOARDING]),
        reading: roundMillisecondsToSeconds(timeTracking[READ_PASSAGE_STEP_NUMBER]),
        ...conjunctionTimes,
      },
      student_highlights: studentHighlights
    }
    const callback = handleFinishActivity ? handleFinishActivity : () => {}
    dispatch(completeActivitySession(sessionID, currentActivity.parent_activity_id, percentage, conceptResults, data, callback))
  }

  function loadPreviousSession(data) {
    const { activeStep } = session;
    const highlights = data.studentHighlights || studentHighlights
    // if the student hasn't gotten to the highlighting stage yet,
    // we don't want them to skip seeing the directions modal and reading the passage again
    const studentHasAtLeastStartedHighlighting = (highlights && highlights.length) || shouldSkipToPrompts
    dispatch(setActiveStepForSession(data.activeStep || activeStep))
    setCompletedSteps(data.completedSteps || completedSteps)
    setTimeTracking(data.timeTracking || timeTracking)
    setStudentHighlights(highlights)
    setHasStartedReadPassageStep(studentHasAtLeastStartedHighlighting)
    setScrolledToEndOfPassage(studentHasAtLeastStartedHighlighting)
    setDoneHighlighting((studentHasAtLeastStartedHighlighting && highlights.length >= MINIMUM_STUDENT_HIGHLIGHT_COUNT) || shouldSkipToPrompts)
  }

  function activateStep(step) { dispatch(setActiveStepForSession(step)) }

  function submitResponse(entry: string, promptID: string, promptText: string, attempt: number, currentActivity: object) {
    const { sessionID, } = session
    const activityUID = getUrlParam('uid', location, isTurk)
    const previousFeedback = session.submittedResponses[promptID] || [];
    // strip any HTML injected by browser extensions (such as Chrome highlight)
    const strippedEntry = safeStripHtml(entry);
    if (activityUID) {
      const args = {
        sessionID,
        activityUID,
        entry: strippedEntry,
        promptID,
        promptText,
        attempt,
        previousFeedback,
        callback: submitResponseCallback,
        activityVersion: currentActivity?.version,
      }
      dispatch(getFeedback(args))
    }
  }

  function trackPassageReadEvent() {
    const { sessionID, } = session
    const activityUID = getUrlParam('uid', location, isTurk)

    if(!previewMode) {
      dispatch(TrackAnalyticsEvent(Events.EVIDENCE_PASSAGE_READ, {
        activityID: activityUID,
        sessionID: sessionID
      }));
    }
  }

  function trackCurrentPromptStartedEvent() {
    const { activeStep } = session;
    const trackingParams = getCurrentStepDataForEventTracking({ activeStep, activities, session, isTurk })
    if (!trackingParams) return; // Bail if there's no data to track

    if(!previewMode) {
      dispatch(TrackAnalyticsEvent(Events.EVIDENCE_PROMPT_STARTED, trackingParams))
    }
  }

  function trackCurrentPromptCompletedEvent() {
    const { activeStep } = session;
    const trackingParams = getCurrentStepDataForEventTracking({ activeStep, activities, session, isTurk })
    if (!trackingParams) return; // Bail if there's no data to track

    if(!previewMode) {
      dispatch(TrackAnalyticsEvent(Events.EVIDENCE_PROMPT_COMPLETED, trackingParams))
    }
  }

  function trackActivityCompletedEvent() {
    const { sessionID, } = session
    const activityID = getUrlParam('uid', location, isTurk)
    if(!previewMode) {
      dispatch(TrackAnalyticsEvent(Events.EVIDENCE_ACTIVITY_COMPLETED, {
        activityID,
        sessionID
      }));
    }
    dispatch(setActivityIsCompleteForSession(true));
    if(!previewMode) {
      defaultHandleFinishActivity()
    } else {
      const ordered = orderedSteps(activities)
      const lastConjunction = ordered[ordered.length - 1]?.conjunction
      if (session.previewSessionStep === lastConjunction) {
        dispatch(setPreviewSessionStep('complete'))
      }
    }
  }

  function completeStep(stepNumber: number) {
    const newCompletedSteps = completedSteps.concat(stepNumber)
    const uniqueCompletedSteps = Array.from(new Set(newCompletedSteps))
    trackCurrentPromptCompletedEvent()
    setCompletedSteps(uniqueCompletedSteps)
    const allSteps = getActivitySteps(activities)
    const lastStep = allSteps[allSteps.length - 1]
    if(stepNumber > READ_PASSAGE_STEP_NUMBER && stepNumber < lastStep) {
      setShowStepsSummary(true);
    }
    if (previewMode) {
      const nextConjunction = orderedSteps(activities)[stepNumber - 1]?.conjunction
      if (nextConjunction) {
        preparePreviewConjunctionStep(nextConjunction)
      }
    }
  }

  function handleClick(e) {
    if (showReadTheDirectionsButton && e.target.className.indexOf('read-the-directions-modal') === -1) {
      handleReadTheDirectionsButtonClick()
    }

    resetTimers(e)
  }

  function handleKeyDown(e) {
    if (e.key !== 'Tab' || showFocusState) { return }

    setShowFocusState(true)
  }

  function handleDoneReadingClick() {
    completeStep(READ_PASSAGE_STEP_NUMBER)
    const scrollContainer = document.getElementsByClassName("read-passage-container")[0]
    scrollContainer.scrollTo(0, 0)
    if(!previewMode) {
      trackPassageReadEvent();
    }
  }

  function onStartReadPassage(e) {
    e.stopPropagation()
    if (previewMode) {
      preparePreviewReadAndHighlightStep()
    } else {
      // Bank the onboarding time first: this click stops before the window listener that would.
      resetTimers()
      setHasStartedReadPassageStep(true)
      setShowReadTheDirectionsButton(true)
    }
  }

  function onStartPromptSteps() {
    if(previewMode) {
      preparePreviewBecauseStep()
    } else {
      setHasStartedPromptsSteps(true)
    }
  }

  function submitProblemReport(args) {
    const { sessionID, } = session
    const lastSubmittedResponse = getLastSubmittedResponse({ activities, session, activeStep });
    const isOptimal = lastSubmittedResponse && lastSubmittedResponse.optimal;
    reportAProblem({...args, sessionID, isOptimal})
  }

  function handleClickDoneHighlighting() {
    setDoneHighlighting(true)
  }

  function scrollToStep(ref: string) {
    if (onMobile()) {
      scrollToStepOnMobile(ref)
    } else {
      const scrollContainer = document.getElementsByClassName("steps-outer-container")[0]
      const el = refs[ref]

      if (scrollContainer) {
        scrollContainer.scrollTo(0, el.offsetTop - 34)
      }
    }
  }

  function scrollToHighlight() {
    const passageHighlights = document.getElementsByClassName('passage-highlight')
    if (!passageHighlights.length) { return }

    const el = passageHighlights[0].parentElement

    // we want to scroll 24px above the top of the paragraph, but we have to use 84 because of the 60px padding set at the top of the activity-container element
    const additionalTopOffset = 84

    if (onMobile()) {
      el.scrollIntoView(true)
      window.scrollBy(0, -additionalTopOffset)
    } else {
      const scrollContainer = document.getElementsByClassName("read-passage-container")[0]
      scrollContainer.scrollTo(0, el.offsetTop - additionalTopOffset)
    }
  }

  function callSaveActiveActivitySession() {
    // we don't want to save an active activity session during preview mode because it will cause inconsistent behavior if page is refreshed
    if(previewMode) { return }

    const args = {
      completedSteps,
      timeTracking,
      studentHighlights,
    }
    dispatch(saveActiveActivitySession(args))
  }

  function submitResponseCallback() {
    callSaveActiveActivitySession()
  }

  function scrollToStepOnMobile(ref: string) {
    refs[ref].current ? refs[ref].current.scrollIntoView(false) : null
  }

  function toggleStudentHighlight(stringText) {
    const textWithHighlightContentRemoved = stringText.replace(STUDENT_HIGHLIGHT_STARTS_TEXT, '').replace(STUDENT_HIGHLIGHT_ENDS_TEXT, '')
    let newHighlights = []

    if (studentHighlights.includes(textWithHighlightContentRemoved)) {
      newHighlights = studentHighlights.filter(hl => hl !== textWithHighlightContentRemoved)
    } else {
      newHighlights = studentHighlights.concat(stringText)
    }

    setStudentHighlights(newHighlights)
  }

  function toggleShowStepsSummary() {
    setShowStepsSummary(!showStepsSummary)
  }

  React.useEffect(() => {
    if (studentHighlights.length === 0 ) { return }
    callSaveActiveActivitySession()
  }, [studentHighlights])

  function handleShowGoodToKnow() {
    if (previewMode) {
      preparePreviewIntroductionStep()
    } else {
      setShowGoodToKnow(true)
    }
  }

  const { submittedResponses, sessionID, activeStep, activityIsComplete } = session;

  if (!activities.hasReceivedData) { return <Spinner /> }

  const className = `activity-container ${showFocusState ? '' : 'hide-focus-outline'} ${activeStep === READ_PASSAGE_STEP_NUMBER ? 'on-read-passage' : ''}`

  if(completeButtonClicked && !window.location.href.includes('turk')) {
    return(
      <ActivityFollowUp activity={activities.currentActivity} dispatch={dispatch} previewMode={previewMode} responses={submittedResponses} saveActivitySurveyResponse={saveActivitySurveyResponse} sessionID={sessionID} userRole={userRole} />
    );
  }

  const completionButtonCallback = () => {
    setCompleteButtonClicked(true)
  }

  return (
    <div className={className}>
      {renderDirections({
        handleReadTheDirectionsButtonClick,
        activeStep,
        doneHighlighting,
        showReadTheDirectionsButton,
        activities,
        hasStartedReadPassageStep,
        hasStartedPromptSteps
      })}
      <ReadPassageContainer
        activeStep={activeStep}
        activities={activities}
        doneHighlighting={doneHighlighting}
        handleReadPassageContainerScroll={handleReadPassageContainerScroll}
        hasStartedPromptSteps={hasStartedPromptSteps}
        hasStartedReadPassageStep={hasStartedReadPassageStep}
        scrolledToEndOfPassage={scrolledToEndOfPassage}
        session={session}
        setScrolledToEndOfPassage={setScrolledToEndOfPassage}
        showReadTheDirectionsButton={showReadTheDirectionsButton}
        studentHighlights={studentHighlights}
        toggleStudentHighlight={toggleStudentHighlight}
      />
      <RightPanel
        activateStep={activateStep}
        activeStep={activeStep}
        activities={activities}
        activityIsComplete={activityIsComplete}
        completedSteps={completedSteps}
        completeStep={completeStep}
        completionButtonCallback={completionButtonCallback}
        doneHighlighting={doneHighlighting}
        handleClickDoneHighlighting={handleClickDoneHighlighting}
        handleDoneReadingClick={handleDoneReadingClick}
        handleReadTheDirectionsButtonClick={handleReadTheDirectionsButtonClick}
        hasStartedPromptSteps={hasStartedPromptSteps}
        hasStartedReadPassageStep={hasStartedReadPassageStep}
        onShowGoodToKnow={handleShowGoodToKnow}
        onStartPromptSteps={onStartPromptSteps}
        onStartReadPassage={onStartReadPassage}
        reportAProblem={submitProblemReport}
        resetTimers={resetTimers}
        scrolledToEndOfPassage={scrolledToEndOfPassage}
        session={session}
        showGoodToKnow={showGoodToKnow}
        showReadTheDirectionsButton={showReadTheDirectionsButton}
        showStepsSummary={showStepsSummary}
        studentHighlights={studentHighlights}
        submitResponse={submitResponse}
        toggleShowStepsSummary={toggleShowStepsSummary}
        toggleStudentHighlight={toggleStudentHighlight}
      />
    </div>
  )

}

const mapStateToProps = (state: any) => {
  return {
    activities: state.activities,
    session: state.session
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    dispatch
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(StudentViewContainer);
